import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Shield, Database, Lock, Mail, Building, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';

const PrivacyPage = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang = i18n.language?.split('-')[0] || 'it';

  const content = {
    it: {
      title: "Informativa sulla Privacy",
      lastUpdate: "Ultimo aggiornamento: Dicembre 2025",
      sections: [
        {
          icon: Building,
          title: "1. Titolare del Trattamento",
          content: `Il Titolare del trattamento dei dati personali è:

**L.S.C. SRL LAVORAZIONI SU CENTRO**
Via Albert Einstein, 6
40050 Monteveglio (BO), Italia
P.IVA: IT01954770366

Per qualsiasi questione relativa al trattamento dei tuoi dati personali, puoi contattarci all'indirizzo: **info@lscsrl.it**`
        },
        {
          icon: Database,
          title: "2. Dati Raccolti",
          content: `Raccogliamo e trattiamo i seguenti tipi di dati:

**Dati Personali:**
• Email di registrazione
• Nome utente (opzionale)
• Dati di fatturazione (gestiti da Stripe)

**Dati Tecnici:**
• Disegni tecnici caricati (PDF, immagini)
• Dati dei progetti (marker, quote, misurazioni)
• Logo aziendale (se caricato)

**Dati di Utilizzo:**
• Conteggio utilizzi AI
• Log di accesso
• Preferenze utente (lingua, piano)`
        },
        {
          icon: Shield,
          title: "3. Finalità del Trattamento",
          content: `I tuoi dati vengono trattati per le seguenti finalità:

**Erogazione del Servizio:**
• Gestione dell'account utente
• Elaborazione e archiviazione dei progetti
• Rilevamento quote tramite intelligenza artificiale
• Generazione di report PDF e Excel

**Gestione Amministrativa:**
• Fatturazione e pagamenti
• Comunicazioni di servizio
• Supporto tecnico

**Miglioramento del Servizio:**
• Analisi aggregate (anonime) per migliorare l'applicazione`
        },
        {
          icon: Lock,
          title: "4. Base Giuridica",
          content: `Il trattamento dei tuoi dati si basa su:

**Contratto (Art. 6.1.b GDPR):**
Il trattamento è necessario per l'esecuzione del contratto di servizio (Termini e Condizioni accettati alla registrazione).

**Interesse Legittimo (Art. 6.1.f GDPR):**
Per il miglioramento del servizio e la prevenzione di abusi, nel rispetto dei tuoi diritti fondamentali.

**Consenso (Art. 6.1.a GDPR):**
Per eventuali comunicazioni promozionali (solo se esplicitamente autorizzate).`
        },
        {
          icon: Globe,
          title: "5. Responsabili del Trattamento",
          content: `Per l'erogazione del servizio, ci avvaliamo dei seguenti fornitori terzi:

**Supabase Inc.**
• Funzione: Database e autenticazione
• Sede: USA (con clausole contrattuali standard UE)
• Privacy: https://supabase.com/privacy

**OpenAI LLC**
• Funzione: Elaborazione AI per rilevamento quote
• Sede: USA (con clausole contrattuali standard UE)
• Privacy: https://openai.com/privacy
• Nota: I tuoi disegni NON vengono usati per addestrare i modelli AI

**Stripe Inc.**
• Funzione: Elaborazione pagamenti
• Sede: USA (certificato Privacy Shield)
• Privacy: https://stripe.com/privacy

**Render Services Inc.**
• Funzione: Hosting dell'applicazione
• Sede: USA
• Privacy: https://render.com/privacy`
        },
        {
          title: "6. Conservazione dei Dati",
          content: `I tuoi dati vengono conservati secondo i seguenti criteri:

**Dati dell'Account:**
Conservati per tutta la durata dell'account attivo.

**Progetti e Disegni:**
Conservati fino alla cancellazione manuale da parte dell'utente o alla chiusura dell'account.

**Dati di Fatturazione:**
Conservati per 10 anni come richiesto dalla normativa fiscale italiana.

**Alla Cancellazione dell'Account:**
Tutti i dati personali e i progetti vengono eliminati entro 30 giorni dalla richiesta.`
        },
        {
          title: "7. I Tuoi Diritti (GDPR)",
          content: `In conformità al Regolamento UE 2016/679 (GDPR), hai diritto a:

**Diritto di Accesso (Art. 15):**
Ottenere conferma dell'esistenza di dati che ti riguardano e accedere a tali dati.

**Diritto di Rettifica (Art. 16):**
Ottenere la correzione di dati inesatti o l'integrazione di dati incompleti.

**Diritto alla Cancellazione (Art. 17):**
Ottenere la cancellazione dei tuoi dati ("diritto all'oblio").

**Diritto alla Portabilità (Art. 20):**
Ricevere i tuoi dati in formato strutturato e trasferirli ad altro titolare.

**Diritto di Opposizione (Art. 21):**
Opporti al trattamento per motivi legittimi.

**Diritto di Reclamo:**
Presentare reclamo all'Autorità Garante per la Protezione dei Dati Personali (www.garanteprivacy.it).`
        },
        {
          title: "8. Sicurezza dei Dati",
          content: `Adottiamo misure tecniche e organizzative appropriate per proteggere i tuoi dati:

• Crittografia HTTPS per tutte le comunicazioni
• Database con crittografia a riposo
• Autenticazione sicura con token JWT
• Accesso ai dati limitato al personale autorizzato
• Backup regolari con crittografia
• Monitoraggio continuo della sicurezza`
        },
        {
          icon: Mail,
          title: "9. Contatti",
          content: `Per esercitare i tuoi diritti o per qualsiasi domanda sulla privacy:

**Email:** info@lscsrl.it

**Indirizzo:**
L.S.C. SRL
Via Albert Einstein, 6
40050 Monteveglio (BO), Italia

Risponderemo alla tua richiesta entro 30 giorni.`
        }
      ]
    },
    en: {
      title: "Privacy Policy",
      lastUpdate: "Last updated: December 2025",
      sections: [
        {
          icon: Building,
          title: "1. Data Controller",
          content: `The Data Controller for your personal data is:

**L.S.C. SRL LAVORAZIONI SU CENTRO**
Via Albert Einstein, 6
40050 Monteveglio (BO), Italy
VAT ID: IT01954770366

For any questions regarding the processing of your personal data, contact us at: **info@lscsrl.it**`
        },
        {
          icon: Database,
          title: "2. Data Collected",
          content: `We collect and process the following types of data:

**Personal Data:**
• Registration email
• Username (optional)
• Billing data (managed by Stripe)

**Technical Data:**
• Uploaded technical drawings (PDF, images)
• Project data (markers, dimensions, measurements)
• Company logo (if uploaded)

**Usage Data:**
• AI usage count
• Access logs
• User preferences (language, plan)`
        },
        {
          icon: Shield,
          title: "3. Purpose of Processing",
          content: `Your data is processed for the following purposes:

**Service Delivery:**
• User account management
• Project processing and storage
• AI-powered dimension detection
• PDF and Excel report generation

**Administrative Management:**
• Billing and payments
• Service communications
• Technical support

**Service Improvement:**
• Aggregate (anonymous) analysis to improve the application`
        },
        {
          icon: Lock,
          title: "4. Legal Basis",
          content: `The processing of your data is based on:

**Contract (Art. 6.1.b GDPR):**
Processing is necessary for the performance of the service contract (Terms accepted at registration).

**Legitimate Interest (Art. 6.1.f GDPR):**
For service improvement and abuse prevention, respecting your fundamental rights.

**Consent (Art. 6.1.a GDPR):**
For any promotional communications (only if explicitly authorized).`
        },
        {
          icon: Globe,
          title: "5. Data Processors",
          content: `We use the following third-party providers:

**Supabase Inc.**
• Function: Database and authentication
• Location: USA (with EU Standard Contractual Clauses)
• Privacy: https://supabase.com/privacy

**OpenAI LLC**
• Function: AI processing for dimension detection
• Location: USA (with EU Standard Contractual Clauses)
• Privacy: https://openai.com/privacy
• Note: Your drawings are NOT used to train AI models

**Stripe Inc.**
• Function: Payment processing
• Location: USA (Privacy Shield certified)
• Privacy: https://stripe.com/privacy

**Render Services Inc.**
• Function: Application hosting
• Location: USA
• Privacy: https://render.com/privacy`
        },
        {
          title: "6. Data Retention",
          content: `Your data is retained according to the following criteria:

**Account Data:**
Retained for the duration of the active account.

**Projects and Drawings:**
Retained until manually deleted by the user or account closure.

**Billing Data:**
Retained for 10 years as required by Italian tax law.

**Upon Account Deletion:**
All personal data and projects are deleted within 30 days of the request.`
        },
        {
          title: "7. Your Rights (GDPR)",
          content: `Under EU Regulation 2016/679 (GDPR), you have the right to:

**Right of Access (Art. 15):**
Obtain confirmation of data concerning you and access such data.

**Right to Rectification (Art. 16):**
Obtain correction of inaccurate data or completion of incomplete data.

**Right to Erasure (Art. 17):**
Obtain deletion of your data ("right to be forgotten").

**Right to Data Portability (Art. 20):**
Receive your data in a structured format and transfer it to another controller.

**Right to Object (Art. 21):**
Object to processing for legitimate reasons.

**Right to Complain:**
File a complaint with the Italian Data Protection Authority (www.garanteprivacy.it).`
        },
        {
          title: "8. Data Security",
          content: `We implement appropriate technical and organizational measures to protect your data:

• HTTPS encryption for all communications
• Database encryption at rest
• Secure JWT token authentication
• Data access limited to authorized personnel
• Regular encrypted backups
• Continuous security monitoring`
        },
        {
          icon: Mail,
          title: "9. Contact",
          content: `To exercise your rights or for any privacy questions:

**Email:** info@lscsrl.it

**Address:**
L.S.C. SRL
Via Albert Einstein, 6
40050 Monteveglio (BO), Italy

We will respond to your request within 30 days.`
        }
      ]
    }
  };

  const c = content[lang] || content.en;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            {lang === 'it' ? 'Indietro' : 'Back'}
          </Button>
          <h1 className="text-xl font-bold text-slate-800">{c.title}</h1>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-800">{c.title}</h2>
              <p className="text-sm text-slate-500">{c.lastUpdate}</p>
            </div>
          </div>

          <div className="space-y-8">
            {c.sections.map((section, index) => (
              <div key={index} className="border-b border-slate-100 pb-6 last:border-0">
                <h3 className="text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  {section.icon && <section.icon className="w-5 h-5 text-blue-500" />}
                  {section.title}
                </h3>
                <div className="text-slate-600 whitespace-pre-line prose prose-sm max-w-none">
                  {section.content.split('**').map((part, i) => 
                    i % 2 === 1 ? <strong key={i}>{part}</strong> : part
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPage;
